import getDownloadedPathsFromCache from '../getDownloadedPathsFromCache';

/**
 * Cache all book document HTML files.
 *
 * @method cacheAllDocuments
 * @returns {Promise<void>}
 */
const cacheAllDocuments = async (book, cache) => {
  if (!book || !cache) {
    return;
  }

  // path for book document file.
  const canonicalPath = (number) => `/spine/${book.pk}/${number}`;

  // Determine which documents are already in the cache.
  const downloadedPaths = await getDownloadedPathsFromCache(cache);

  const documentNumbersToFetch = [];

  for (let bookDocument of book.documents) {
    if (!downloadedPaths.has(canonicalPath(bookDocument.number))) {
      documentNumbersToFetch.push(bookDocument.number);
    }
  }

  if (!documentNumbersToFetch.length) {
    // No documents to fetch.
    return;
  }

  // Get JSON payloads of S3 URLs for all documents in the book.
  // The spine list is paginated with 100 documents per request because it takes the S3 API some time to generate the list.
  // If it tries to generate too many at once it will time out. 100 is a safe number.
  const spineListPageCount = Math.ceil(book.documents.length / 100);
  const documentUrls = {};

  if (!book.isPublisherEncryptionEnabled) {
    for (let i = 0; i < spineListPageCount; i++) {
      Object.assign(
        documentUrls,
        await fetch(`/spine-list/${book.pk}/${i + 1}`).then((response) =>
          response.json(),
        ),
      );
    }
  }

  // Fetch each document and add it to the cache.
  for (let documentNumber of documentNumbersToFetch) {
    try {
      // Allow falling back to indirect (through Django app) path for titles with publisher encryption enabled.
      fetch(documentUrls[documentNumber] ?? canonicalPath(documentNumber), {
        redirect: 'follow',
      }).then(async (response) => {
        if (!response.ok) {
          throw new Error('request failed');
        }

        // Clone the response so that it can be fetched by the service worker.
        // Otherwise, the AWS S3 redirection will cause security issues.
        const clonedResponse = response.clone();

        const body =
          'body' in clonedResponse
            ? clonedResponse.body
            : await clonedResponse.blob();

        // Remove all headers that start with X-*. They don't hurt anything, but they are not necessary.
        // If the response was not stored using this flow, it may have a redirected=true property.
        // That redirected=true property is not visible in the devtools without adding logpoints,
        // and it is nefarious as it will break the reader with no indication as to why.
        // So removing the AWS X-* headers provides an indication that the response was stored using this flow.
        // And so if headers such as X-Amz-request-id are present, it shows that something went wrong.
        // i.e., the resource was cached with code elsewhere in the reader that did not use this cloning logic.
        const headers = new Headers();
        for (const [key, value] of clonedResponse.headers.entries()) {
          if (!key.toLowerCase().startsWith('x-')) {
            headers.append(key, value);
          }
        }

        return cache.put(
          canonicalPath(documentNumber),
          new Response(body, {
            headers,
            status: clonedResponse.status,
            statusText: clonedResponse.statusText,
          }),
        );
      });
    } catch (error) {
      console.warn(error);
    }
  }
};

export default cacheAllDocuments;
